import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { AnimatePresence } from 'framer-motion'

import Button from '../button/Button'
import TextField from '../text-field/TextField'
import CreditCheckPaymentStep from './CreditCheckPaymentStep'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { orderCreditCheck } from '../../_data/verifications'
import {
  APPLICANT_TITLES,
  CANADIAN_DIVISIONS,
  CONSENT_WORDING,
  CONSENT_WORDING_VERSION,
  CREDIT_CHECK_PRICE_CENTS,
  createEmptyResidence,
  findValidationError,
  formatPrice,
  toResidencePayloads,
} from './helpers'
import {
  CertnApplicantTitle,
  CreditCheckFormState,
  CreditCheckModalProps,
  CreditCheckResidence,
} from './types'
import {
  AddButton,
  Backdrop,
  Card,
  ConsentBox,
  ConsentCheckbox,
  ConsentLabel,
  ConsentText,
  CostNote,
  ErrorBanner,
  FieldGroup,
  FieldLabel,
  FieldRow,
  Footer,
  Form,
  Header,
  PriceBox,
  PriceTotalRow,
  RemoveButton,
  ResidenceCard,
  ResidenceHeader,
  ResidenceLabel,
  SectionTitle,
  Select,
  Subtitle,
  Title,
} from './styled'

const createInitialState = (): CreditCheckFormState => ({
  step: 'details',
  title: '',
  residences: [createEmptyResidence()],
  socialInsuranceNumber: '',
  hasGrantedConsent: false,
  errorMessage: '',
  isSubmitting: false,
})

/**
 * Collects everything Certn needs to run a Canadian credit check, captures the
 * renter's consent, takes the fee, and places the order.
 *
 * QuickPass orders by quickscreen, which means Certn never shows the renter a
 * consent form of its own. The wording in this modal is the only consent the
 * renter ever sees, so it is displayed in full rather than hidden behind a
 * link, and the renter cannot move on to payment until it is accepted.
 *
 * Details and consent come first and the card second, so somebody who decides
 * against the check partway through is never asked to pay for it.
 *
 * @param {CreditCheckModalProps} props
 * @returns {JSX.Element | null} null while closed
 */
const CreditCheckModal = ({
  isOpen,
  quickPassUserId,
  onClose,
  onOrdered,
}: CreditCheckModalProps): JSX.Element | null => {
  const { refreshProfile } = useQuickPassProfile()
  const [state, setState] = useState<CreditCheckFormState>(createInitialState)

  useEffect(() => {
    if (isOpen === false) {
      setState(createInitialState())
    }
  }, [isOpen])

  if (isOpen === false) {
    return null
  }

  const updateResidence = (
    index: number,
    change: Partial<CreditCheckResidence>,
  ): void => {
    setState(previous => ({
      ...previous,
      residences: previous.residences.map((residence, residenceIndex) =>
        residenceIndex === index ? { ...residence, ...change } : residence,
      ),
    }))
  }

  const updateResidenceAddress = (
    index: number,
    field: keyof CreditCheckResidence['address'],
    value: string,
  ): void => {
    setState(previous => ({
      ...previous,
      residences: previous.residences.map((residence, residenceIndex) =>
        residenceIndex === index
          ? { ...residence, address: { ...residence.address, [field]: value } }
          : residence,
      ),
    }))
  }

  const handleAddResidence = (): void => {
    setState(previous => ({
      ...previous,
      residences: [...previous.residences, createEmptyResidence()],
    }))
  }

  const handleRemoveResidence = (index: number): void => {
    setState(previous => ({
      ...previous,
      residences: previous.residences.filter(
        (_residence, residenceIndex) => residenceIndex !== index,
      ),
    }))
  }

  const handleContinueToPayment = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()

    const validationError = findValidationError(
      state.title,
      state.residences,
      state.socialInsuranceNumber,
      state.hasGrantedConsent,
    )

    if (validationError !== '') {
      setState(previous => ({ ...previous, errorMessage: validationError }))
      return
    }

    setState(previous => ({ ...previous, errorMessage: '', step: 'payment' }))
  }

  const handleAuthorized = async (paymentIntentId: string): Promise<void> => {
    setState(previous => ({
      ...previous,
      errorMessage: '',
      isSubmitting: true,
    }))

    const result = await orderCreditCheck({
      quickPassUserId,
      title: state.title,
      residences: toResidencePayloads(state.residences),
      socialInsuranceNumber: state.socialInsuranceNumber.trim(),
      consentWordingVersion: CONSENT_WORDING_VERSION,
      hasGrantedConsent: state.hasGrantedConsent,
      paymentIntentId,
    })

    if (result === null) {
      // The server releases the hold on any failure, so this promise is one we
      // can actually keep. Saying it plainly matters: the renter has just
      // watched their card get authorised.
      setState(previous => ({
        ...previous,
        isSubmitting: false,
        step: 'details',
        errorMessage:
          'We could not start your credit check, so the hold on your card has been released and you have not been charged. Please try again in a moment.',
      }))
      return
    }

    await refreshProfile()
    setState(previous => ({ ...previous, isSubmitting: false }))
    onOrdered(
      'Your credit check is on its way. We will update your QuickPass as soon as the result lands.',
    )
    onClose()
  }

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Card
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          onClick={clickEvent => {
            clickEvent.stopPropagation()
          }}
        >
          <Header>
            <Title>Run a credit check</Title>
            <Subtitle>
              Landlords trust a verified credit check far more than a number you
              type in yourself. Certn runs it, and your QuickPass shows a
              summary only, never your full report.
            </Subtitle>
          </Header>

          {state.step === 'payment' ? (
            <CreditCheckPaymentStep
              quickPassUserId={quickPassUserId}
              isPlacingOrder={state.isSubmitting}
              onBack={() => {
                setState(previous => ({ ...previous, step: 'details' }))
              }}
              onAuthorized={paymentIntentId => {
                void handleAuthorized(paymentIntentId)
              }}
            />
          ) : (
            <Form onSubmit={handleContinueToPayment}>
              <FieldLabel>
                Title
                <Select
                  value={state.title}
                  onChange={(changeEvent: ChangeEvent<HTMLSelectElement>) => {
                    setState(previous => ({
                      ...previous,
                      title: changeEvent.target.value as CertnApplicantTitle,
                    }))
                  }}
                >
                  <option value="">Select a title</option>
                  {APPLICANT_TITLES.map(title => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </Select>
              </FieldLabel>

              <SectionTitle>Where you live</SectionTitle>

              <FieldGroup>
                {state.residences.map((residence, index) => (
                  <ResidenceCard key={`residence-${String(index)}`}>
                    <ResidenceHeader>
                      <ResidenceLabel>
                        {index === 0 ? 'Current address' : 'Previous address'}
                      </ResidenceLabel>
                      {index > 0 ? (
                        <RemoveButton
                          type="button"
                          onClick={() => {
                            handleRemoveResidence(index)
                          }}
                        >
                          Remove
                        </RemoveButton>
                      ) : null}
                    </ResidenceHeader>

                    <TextField
                      label="Street address"
                      value={residence.address.streetAddress}
                      onChange={changeEvent => {
                        updateResidenceAddress(
                          index,
                          'streetAddress',
                          changeEvent.target.value,
                        )
                      }}
                    />

                    <FieldRow>
                      <TextField
                        label="Unit (optional)"
                        value={residence.address.unit}
                        onChange={changeEvent => {
                          updateResidenceAddress(
                            index,
                            'unit',
                            changeEvent.target.value,
                          )
                        }}
                      />
                      <TextField
                        label="City"
                        value={residence.address.locality}
                        onChange={changeEvent => {
                          updateResidenceAddress(
                            index,
                            'locality',
                            changeEvent.target.value,
                          )
                        }}
                      />
                    </FieldRow>

                    <FieldRow>
                      <FieldLabel>
                        Province or territory
                        <Select
                          value={residence.address.administrativeDivision}
                          onChange={(
                            changeEvent: ChangeEvent<HTMLSelectElement>,
                          ) => {
                            updateResidenceAddress(
                              index,
                              'administrativeDivision',
                              changeEvent.target.value,
                            )
                          }}
                        >
                          {CANADIAN_DIVISIONS.map(division => (
                            <option key={division.code} value={division.code}>
                              {division.name}
                            </option>
                          ))}
                        </Select>
                      </FieldLabel>
                      <TextField
                        label="Postal code"
                        value={residence.address.postalCode}
                        onChange={changeEvent => {
                          updateResidenceAddress(
                            index,
                            'postalCode',
                            changeEvent.target.value,
                          )
                        }}
                      />
                    </FieldRow>

                    <FieldRow>
                      <TextField
                        label="Moved in"
                        type="month"
                        value={residence.movedInMonth}
                        onChange={changeEvent => {
                          updateResidence(index, {
                            movedInMonth: changeEvent.target.value,
                          })
                        }}
                      />
                      {index > 0 ? (
                        <TextField
                          label="Moved out"
                          type="month"
                          value={residence.movedOutMonth}
                          onChange={changeEvent => {
                            updateResidence(index, {
                              movedOutMonth: changeEvent.target.value,
                            })
                          }}
                        />
                      ) : null}
                    </FieldRow>
                  </ResidenceCard>
                ))}

                <AddButton type="button" onClick={handleAddResidence}>
                  Add a previous address
                </AddButton>
              </FieldGroup>

              <TextField
                label="Social insurance number (optional)"
                value={state.socialInsuranceNumber}
                helperText="Optional, and it helps the credit bureau find the right file. You can leave it blank."
                onChange={changeEvent => {
                  setState(previous => ({
                    ...previous,
                    socialInsuranceNumber: changeEvent.target.value,
                  }))
                }}
              />

              <ConsentBox>
                <ConsentText>{CONSENT_WORDING}</ConsentText>
                <ConsentLabel>
                  <ConsentCheckbox
                    type="checkbox"
                    checked={state.hasGrantedConsent}
                    onChange={changeEvent => {
                      setState(previous => ({
                        ...previous,
                        hasGrantedConsent: changeEvent.target.checked,
                      }))
                    }}
                  />
                  I have read the statement above and I agree to it.
                </ConsentLabel>
              </ConsentBox>

              <PriceBox>
                <PriceTotalRow>
                  <span>Credit check</span>
                  <span>{formatPrice(CREDIT_CHECK_PRICE_CENTS)}</span>
                </PriceTotalRow>
              </PriceBox>

              <CostNote>
                One flat price, nothing added at checkout. The credit bureau
                needs your identity verified before it will release a report, so
                that runs alongside your credit check at no extra cost to you.
              </CostNote>

              {state.errorMessage !== '' ? (
                <ErrorBanner>{state.errorMessage}</ErrorBanner>
              ) : null}

              <Footer>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="small"
                  disabled={state.hasGrantedConsent === false}
                >
                  Continue to payment
                </Button>
              </Footer>
            </Form>
          )}
        </Card>
      </Backdrop>
    </AnimatePresence>
  )
}

export default CreditCheckModal
